/**
 * Automated calls, as Connect's staff see them: the installation's provider and
 * account (`/calling/admin`), and one client's switch and calls
 * (`/calling/admin/clients/{id}`). The API never sends the Exotel token back —
 * only whether one is stored.
 */

export interface CallingPlatform {
  enabled: boolean;
  provider: string | null;
  caller_id: string | null;
  max_attempts: number;
  retry_minutes: number;
  window_start: string;
  window_end: string;
  exotel: {
    host: string;
    account_sid: string;
    api_key: string;
    flow_id: string;
    api_token_set: boolean;
  };
}

export interface CallingOverview {
  platform: CallingPlatform;
  providers: { id: string; label: string }[];
  ready: boolean;
  /** What Exotel's call flow is given, once. Absent until the first save. */
  flow_addresses: { gather: string; passthru: string; greeting: string; status: string } | null;
}

export interface CallRow {
  public_id: string;
  to_number: string;
  purpose: string;
  purpose_label: string;
  status: string;
  attempts: number;
  max_attempts: number;
  pressed: string | null;
  pressed_label: string | null;
  duration_seconds: number | null;
  next_attempt_at: string | null;
  answered_at: string | null;
  ended_at: string | null;
  created_at: string;
}

export interface CallingClient {
  public_id: string;
  name: string;
  calling_enabled: boolean;
  platform_ready: boolean;
  calls: { items: CallRow[]; has_more: boolean };
}

export const EXOTEL_HOSTS = [
  { value: 'api.exotel.com', label: 'Singapore (api.exotel.com)' },
  { value: 'api.in.exotel.com', label: 'Mumbai (api.in.exotel.com)' },
] as const;

export const callingKeys = {
  overview: ['calling', 'overview'] as const,
  client: (publicId: string) => ['calling', 'client', publicId] as const,
};

/** A call's state, in a word and a tone. */
export const CALL_STATUS: Record<string, { label: string; tone: 'neutral' | 'info' | 'success' | 'warning' | 'danger' }> = {
  queued: { label: 'Waiting', tone: 'neutral' },
  dialing: { label: 'Calling', tone: 'info' },
  ringing: { label: 'Ringing', tone: 'info' },
  answered: { label: 'On the call', tone: 'info' },
  completed: { label: 'Answered', tone: 'success' },
  no_answer: { label: 'No answer', tone: 'warning' },
  busy: { label: 'Busy', tone: 'warning' },
  failed: { label: 'Failed', tone: 'danger' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
};
